/**
 * Tolerant parser for Pure LLM chart mutation markup.
 *
 * LLM responses may embed self-closing tags such as:
 *   <chart-op type="add-entity" ref="Q123" label="John Doe" x="420" y="180" />
 *   <chart-op type="add-arrow" from="Q123" to="Q456" label="member of" />
 *   <chart-op type="add-entity" ref="Q789" label="Cartel X" near="Q878" placement="below-right" />
 *   <chart-op type="update-arrow" from="Q1" to="Q2" label="former member of" />
 *   <chart-op type="move-entity" ref="Q123" x="420" y="180" />
 *   <chart-op type="remove-entity" ref="Q123" />
 *   <chart-op type="remove-arrow" from="Q123" to="Q456" label="member of" />
 *
 * Aliases: delete-entity → remove-entity; delete-arrow → remove-arrow.
 * Tags are stripped from display markdown; ops + parse warnings are returned separately.
 */

const CHART_OP_TAG_RE = /<chart-op\b([^>]*?)\s*\/?\s*>/gi;

const ATTR_RE = /([a-zA-Z_:][-a-zA-Z0-9_:.]*)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+))/g;

const TYPE_ALIASES = {
    'delete-entity': 'remove-entity',
    'delete-arrow': 'remove-arrow',
};

const SUPPORTED_TYPES = new Set([
    'add-entity',
    'add-arrow',
    'update-arrow',
    'create-local',
    'move-entity',
    'remove-entity',
    'remove-arrow',
]);
const PLACEMENT_VALUES = new Set([
    'above',
    'below',
    'left',
    'right',
    'above-left',
    'above-right',
    'below-left',
    'below-right',
]);

/**
 * @param {string|null|undefined} from
 * @param {string|null|undefined} to
 * @returns {boolean}
 */
export function isSelfReferentialArrow(from, to) {
    if (from == null || to == null) {
        return false;
    }
    const fromRef = String(from).trim();
    const toRef = String(to).trim();
    if (!fromRef || !toRef) {
        return false;
    }
    const normalizedFrom = /^q\d+$/i.test(fromRef) ? fromRef.toUpperCase() : fromRef;
    const normalizedTo = /^q\d+$/i.test(toRef) ? toRef.toUpperCase() : toRef;
    return normalizedFrom === normalizedTo;
}

/**
 * @param {string|null|undefined} attrBlob
 * @returns {Record<string, string>}
 */
export function parseChartOpAttributes(attrBlob) {
    const attrs = {};
    if (!attrBlob || typeof attrBlob !== 'string') {
        return attrs;
    }
    ATTR_RE.lastIndex = 0;
    let match;
    while ((match = ATTR_RE.exec(attrBlob)) !== null) {
        const key = String(match[1] || '').trim().toLowerCase();
        const value = match[2] ?? match[3] ?? match[4] ?? '';
        if (key) {
            attrs[key] = String(value);
        }
    }
    return attrs;
}

/**
 * @param {string|null|undefined} value
 * @returns {number|null}
 */
function parseFiniteNumber(value) {
    if (value == null || value === '') {
        return null;
    }
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}

/**
 * LLM models sometimes emit alternate attribute names (op/id instead of type/ref).
 * @param {Record<string, string>} attrs
 * @returns {Record<string, string>}
 */
export function normalizeChartOpAttributeAliases(attrs) {
    const out = {...attrs};
    if (!out.type) {
        out.type = out.op || out.action || out.operation || '';
    }
    if (!out.ref) {
        out.ref = out.id || out.entity || out.entityref || out['entity-ref'] || '';
    }
    if (!out.from && out.source) {
        out.from = out.source;
    }
    if (!out.to && out.target) {
        out.to = out.target;
    }
    return out;
}

/**
 * Normalize a single chart-op attribute map into a structured op, or null with a warning.
 *
 * @param {Record<string, string>} attrs
 * @param {number} index — 1-based tag index for warnings
 * @returns {{op: object|null, warning: string|null}}
 */
export function normalizeChartOp(attrs, index = 1) {
    const normalizedAttrs = normalizeChartOpAttributeAliases(attrs);
    const rawType = String(normalizedAttrs.type || '').trim().toLowerCase().replace(/_/g, '-');
    const type = TYPE_ALIASES[rawType] || rawType;
    if (!type) {
        return {op: null, warning: `chart-op #${index}: missing type`};
    }
    if (!SUPPORTED_TYPES.has(type)) {
        return {op: null, warning: `chart-op #${index}: unsupported type "${rawType || type}"`};
    }

    const label = normalizedAttrs.label != null ? String(normalizedAttrs.label).trim() : '';
    const base = {type, rawAttrs: {...normalizedAttrs}};

    if (type === 'add-entity' || type === 'create-local') {
        const ref = normalizedAttrs.ref != null ? String(normalizedAttrs.ref).trim() : '';
        if (type === 'add-entity' && !ref) {
            return {op: null, warning: `chart-op #${index}: add-entity requires ref`};
        }
        if (type === 'create-local' && !label) {
            return {op: null, warning: `chart-op #${index}: create-local requires label`};
        }
        const x = parseFiniteNumber(normalizedAttrs.x);
        const y = parseFiniteNumber(normalizedAttrs.y);
        const near = normalizedAttrs.near != null ? String(normalizedAttrs.near).trim() : '';
        const placementRaw = normalizedAttrs.placement != null
            ? String(normalizedAttrs.placement).trim().toLowerCase()
            : '';
        const placement = PLACEMENT_VALUES.has(placementRaw) ? placementRaw : '';

        /** @type {object} */
        const op = {
            ...base,
            ref: ref || null,
            label: label || null,
        };
        if (x != null) op.x = x;
        if (y != null) op.y = y;
        if (near) op.near = near;
        if (placement) op.placement = placement;

        if ((x != null) !== (y != null)) {
            delete op.x;
            delete op.y;
            return {
                op,
                warning: `chart-op #${index}: ${type} needs both x and y; ignoring partial coordinates`,
            };
        }
        if (x == null && y == null && !near) {
            return {
                op,
                warning: `chart-op #${index}: ${type} has no x/y or near — applier will use viewport fallback`,
            };
        }
        if (near && placementRaw && !placement) {
            return {
                op,
                warning: `chart-op #${index}: unknown placement "${placementRaw}" — defaulting relative to near`,
            };
        }
        return {op, warning: null};
    }

    if (type === 'add-arrow' || type === 'update-arrow') {
        const from = normalizedAttrs.from != null ? String(normalizedAttrs.from).trim() : '';
        const to = normalizedAttrs.to != null ? String(normalizedAttrs.to).trim() : '';
        if (!from || !to) {
            return {
                op: null,
                warning: `chart-op #${index}: ${type} requires from and to`,
            };
        }
        if (type === 'add-arrow' && isSelfReferentialArrow(from, to)) {
            return {
                op: null,
                warning: `chart-op #${index}: ${type} cannot connect an entity to itself`,
            };
        }
        return {
            op: {
                ...base,
                from,
                to,
                label: label || null,
            },
            warning: null,
        };
    }

    if (type === 'move-entity') {
        const ref = normalizedAttrs.ref != null ? String(normalizedAttrs.ref).trim() : '';
        if (!ref) {
            return {op: null, warning: `chart-op #${index}: move-entity requires ref`};
        }
        const x = parseFiniteNumber(normalizedAttrs.x);
        const y = parseFiniteNumber(normalizedAttrs.y);
        if (x == null || y == null) {
            return {
                op: null,
                warning: `chart-op #${index}: move-entity requires both x and y`,
            };
        }
        return {
            op: {
                ...base,
                ref,
                x,
                y,
            },
            warning: null,
        };
    }

    if (type === 'remove-entity') {
        const ref = normalizedAttrs.ref != null ? String(normalizedAttrs.ref).trim() : '';
        if (!ref) {
            return {op: null, warning: `chart-op #${index}: remove-entity requires ref`};
        }
        return {
            op: {
                ...base,
                ref,
                label: label || null,
            },
            warning: null,
        };
    }

    if (type === 'remove-arrow') {
        const from = normalizedAttrs.from != null ? String(normalizedAttrs.from).trim() : '';
        const to = normalizedAttrs.to != null ? String(normalizedAttrs.to).trim() : '';
        if (!from || !to) {
            return {
                op: null,
                warning: `chart-op #${index}: remove-arrow requires from and to`,
            };
        }
        return {
            op: {
                ...base,
                from,
                to,
                label: label || null,
            },
            warning: null,
        };
    }

    return {op: null, warning: `chart-op #${index}: unsupported type "${type}"`};
}

/**
 * Parse LLM markdown that may contain `<chart-op … />` tags.
 *
 * @param {string|null|undefined} markdown
 * @returns {{
 *   ops: Array<object>,
 *   displayMarkdown: string,
 *   warnings: string[],
 *   rawMarkup: string,
 * }}
 */
export function parseChartMutationMarkup(markdown) {
    const source = typeof markdown === 'string' ? markdown : '';
    const ops = [];
    const warnings = [];
    const rawParts = [];

    CHART_OP_TAG_RE.lastIndex = 0;
    let match;
    let index = 0;
    while ((match = CHART_OP_TAG_RE.exec(source)) !== null) {
        index += 1;
        rawParts.push(match[0]);
        const attrs = parseChartOpAttributes(match[1]);
        const {op, warning} = normalizeChartOp(attrs, index);
        if (warning) {
            warnings.push(warning);
        }
        if (op) {
            ops.push(op);
        }
    }

    CHART_OP_TAG_RE.lastIndex = 0;
    const displayMarkdown = source
        .replace(CHART_OP_TAG_RE, '')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();

    return {
        ops,
        displayMarkdown,
        warnings,
        rawMarkup: rawParts.join('\n'),
    };
}

export {SUPPORTED_TYPES, PLACEMENT_VALUES, TYPE_ALIASES};

export default parseChartMutationMarkup;
